import * as moment from "moment-timezone";
import { useMemo, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { classNames, useAppConfig } from "@ct-react/core";
import { useLocaleContext } from "@ct-react/locale";
import { CustomerReview, extractCmsContent } from "@shared/graphql";
import { Wrapper } from "../layout/wrapper";
import ReviewCard from "../review-card";
import ReviewsIntoModal, { ReviewsHandler } from "../article/reviews-into-modal";
import "./reviews.scss";

const transDefs = defineMessages({
  title: { id: "homepage.reviews.title", defaultMessage: "Votre satisfaction, notre engagement" },
  subTitle: { id: "homepage.reviews.sub-title", defaultMessage: "Vos avis" },
});

const REVIEWS_LIST_GQL_DATA = gql`
  query GetEditorializedReviews($templateId: UUID!) {
    reviews: cmsPages(templateId: $templateId) { data }
  }
`;

const ReviewsPart = ({ className }: { className?: string }) => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { options: { cmsReviewsTemplateId: templateId } } = useAppConfig();
  const modalRef = useRef<ReviewsHandler>(null);

  const { loading, data } = useQuery<{ reviews: any[] }>(REVIEWS_LIST_GQL_DATA, {
    ssr: false,
    variables: { templateId }
  });

  const content = useMemo<CustomerReview[]>(() => {
    if (!data?.reviews || !data?.reviews[0]) return [];
    return extractCmsContent(data.reviews[0].data, locale)["reviews-items"]
      .reverse()
      .map((item: any) => {
        const averageRate = Number(item["review-rate"]);
        const reviewDate = moment(item["review-complement"], "MMMM YYYY", locale.basename);
        return {
          customerName: item["review-customer"],
          ...(!isNaN(averageRate) && { averageRate }),
          comment: item["review-comment"],
          reviewedAt: reviewDate.isValid() ? reviewDate : item["review-complement"]
        }
      })
      .sort(({ reviewedAt: a }: CustomerReview, { reviewedAt: b }: CustomerReview) => {
        if (moment.isMoment(a) && moment.isMoment(b))
          return b.valueOf() - a.valueOf();
        return 0;
      });
  }, [ data, locale ]);

  if (!loading && content.length === 0)
    return null;

  const wrapperClasses = classNames("reviews-part", "content", className);

  return (
    <>
      <Wrapper.ScrollableList as="section" className={wrapperClasses}>
        <Wrapper.ScrollableListHeader title={intl.formatMessage(transDefs.title)}
                                      subTitle={intl.formatMessage(transDefs.subTitle)} />
        <Wrapper.ScrollableListContent>
          {(content.length > 0 ? content : Array.from({ length: 5 })).map((item: any, index: number) => (
            <ReviewCard key={item?.customerName || index}
                        loading={loading} data={item}
                        onMore={() => modalRef.current?.display(index)} />)
          )}
        </Wrapper.ScrollableListContent>
      </Wrapper.ScrollableList>
      <ReviewsIntoModal ref={modalRef} data={content} />
    </>);

}

export default ReviewsPart;
